import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type RecordTypeOption,
  RecordTypePickerModal,
} from '@/components/kartoteki/RecordTypePickerModal';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { narrowDocumentTypes } from '@/lib/documents/narrow-document-types';
import { useActiveCompanyScope } from '@/lib/use-active-company';
import { type DocumentClass, type DocumentType, useDocumentTypes } from '@/lib/use-document-types';
import { useQuickList } from '@/lib/use-quick-list';

interface Props {
  open: boolean;
  docClass: DocumentClass;
  onSelect: (documentTypeId: string, companyId: string) => void;
  onClose: () => void;
  /** Firma dokumentu, z którego modal jest otwierany (np. formatka create) — domyślna wartość filtra firmy. */
  initialCompanyId?: string | null;
  /** Aktualny typ dokumentu, z którego modal jest otwierany — wstępnie zaznaczany wiersz. */
  initialDocumentTypeId?: string | null;
  /** Nadpisanie etykiety przycisku potwierdzenia — domyślnie "Otwórz formularz" (tryb create);
   * D.8 krok 2 (zmiana typu na zapisanym dokumencie) podaje np. "Zmień typ". */
  confirmLabel?: string;
  /**
   * Zawężenie listy do typów dokumentu z zewnętrznego kontekstu (Etap 3, blok C —
   * `useDocumentTypeNarrowing` na liście, z której modal jest otwierany: URL
   * `?documentTypeIds=`/legacy `?documentTypeId=` + filtr stały pozycji menu). Belt-and-suspenders
   * na wierzchu istniejących filtrów tekstowych — „Dodaj" na listach zawsze otwiera modal
   * (bez skrótu pomijającego wybór typu), a lista w modalu pokazuje WYŁĄCZNIE dozwolone typy.
   * Zawężenie obejmuje też lokalne kopie typów globalnych z zawężenia (ten sam kod w klasie —
   * reguła jak na serwerze, patrz `narrowDocumentTypes`). Pusta tablica/`undefined`
   * = brak zawężenia (zachowanie bez zmian).
   */
  allowedTypeIds?: string[];
}

export function DocumentTypePickerModal({
  open,
  docClass,
  onSelect,
  onClose,
  initialCompanyId = null,
  initialDocumentTypeId = null,
  confirmLabel,
  allowedTypeIds,
}: Props) {
  const { t } = useTranslation();
  const { me } = useTenantAuth();

  const companies = useMemo(
    () => [...(me?.companies ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [me?.companies],
  );

  // Modal zawsze pobiera UNIĘ typów po wszystkich firmach widocznych dla usera
  // (companyId pominięty w query do GET /api/document-types) — niezależnie od trybu
  // globalnego przełącznika firmy w topbarze. Zawężenie do jednej firmy jest lokalnym
  // tekstowym filtrem (`filterCompany` we wspólnym oknie `RecordTypePickerModal`), nie
  // parametrem zapytania — dzięki temu modal może pokazać/zaznaczyć typ firmy dokumentu,
  // z którego został otwarty, nawet gdy różni się od aktywnej firmy w topbarze. Typy
  // globalne są materializowane pod tym samym id per firma, więc klucz wiersza/testid/
  // pin-lookup musi być kompozytowy (id, companyId) — patrz `rowKey` we wspólnym oknie.
  const companyScope = useActiveCompanyScope();

  const {
    data: rawTypes,
    allRows: allTypeRows,
    isLoading,
    isError,
  } = useDocumentTypes(docClass, {
    companyId: null,
  });

  // Wiersz typu bez firmy (relikt sprzed wymogu obowiązkowej firmy przy tworzeniu typu —
  // dziś POST /document-types zawsze wymaga companyId) nie może spełnić inwariantu
  // "utworzenie dokumentu zawsze niesie konkretną firmę" — pomijamy go całkowicie
  // zamiast cicho degradować kontrakt onSelect(documentTypeId, companyId).
  //
  // `allowedTypeIds` (Etap 3, blok C) zawęża dalej do typów dozwolonych przez kontekst
  // zewnętrzny (URL/filtr stały pozycji menu, z której modal został otwarty) — belt-and-
  // -suspenders na wierzchu tego samego filtra. Gdy wynik ma dokładnie jeden wiersz, efekt
  // auto-zaznaczenia niżej (fallback na `types[0]`) sam go preselektuje.
  //
  // Zawężenie idzie TĄ SAMĄ regułą co lista dokumentów na serwerze (`narrowDocumentTypes`):
  // id z zawężenia to zwykle id typów GLOBALNYCH, a firma, która skopiowała typ globalny, ma
  // własną kopię pod INNYM id (ten sam kod) — przesłania ona typ globalny w tej firmie (wiersz
  // globalny jest wtedy niewybieralny i hook go odsiewa), więc samo porównanie id wycinałoby
  // kopię i picker twierdziłby „żaden typ nie pasuje", gdy lista pokazuje dokumenty tej firmy.
  const types = useMemo(() => {
    const withCompany = rawTypes.filter(
      (dt): dt is DocumentType & { companyId: string } => dt.companyId !== null,
    );
    return narrowDocumentTypes(withCompany, allTypeRows, allowedTypeIds ?? []);
  }, [rawTypes, allTypeRows, allowedTypeIds]);

  // Rozróżnienie pustego stanu (blok C, finding [C]): „typy istnieją, ale bieżące
  // zawężenie (URL / filtr stały pozycji menu) wyklucza wszystkie" ≠ „brak typów w ogóle".
  // Prawda tylko gdy `allowedTypeIds` faktycznie coś wyciął (były wiersze przed zawężeniem).
  const narrowingExcludesAll = useMemo(
    () =>
      !!allowedTypeIds &&
      allowedTypeIds.length > 0 &&
      types.length === 0 &&
      rawTypes.some((dt) => dt.companyId !== null),
    [allowedTypeIds, types.length, rawTypes],
  );

  const { pinnedIds, isLoading: isQuickListLoading, pin, unpin } = useQuickList('document_type');

  // Domyślna wartość pola „Firma" przy otwarciu: pierwszeństwo ma firma dokumentu, z którego modal
  // jest otwierany (`initialCompanyId`, np. formatka create); bez niej spada na wejście z listy —
  // aktywną firmę z topbara, gdy user nie jest w trybie „Wszystkie firmy".
  const defaultFilterCompanyId =
    initialCompanyId ?? (companyScope.mode === 'company' ? companyScope.companyId : null);

  const options = useMemo<RecordTypeOption[]>(
    () =>
      types.map((dt) => ({
        id: dt.id,
        name: dt.name,
        code: dt.code,
        companyId: dt.companyId,
        companyName: companies.find((c) => c.id === dt.companyId)?.name ?? '',
        description: dt.description,
        origin: {
          isGlobal: dt.origin === 'global',
          label:
            dt.origin === 'global'
              ? t('tenant.documents.typePicker.origin.system')
              : t('tenant.documents.typePicker.origin.own'),
        },
      })),
    [types, companies, t],
  );

  return (
    <RecordTypePickerModal
      open={open}
      onClose={onClose}
      onSelect={onSelect}
      testIdPrefix="document-type-picker"
      types={options}
      isLoading={isLoading}
      isError={isError}
      companies={companies}
      defaultFilterCompanyId={defaultFilterCompanyId}
      initialTypeId={initialDocumentTypeId}
      initialCompanyId={initialCompanyId}
      favorites={{ pinnedIds, isLoading: isQuickListLoading, pin, unpin }}
      labels={{
        title: t('tenant.documents.typePicker.title'),
        confirm: confirmLabel ?? t('tenant.documents.typePicker.confirm'),
        allCompaniesHint: t('tenant.documents.typePicker.pickTypeForCompany'),
        empty: narrowingExcludesAll
          ? t('tenant.documents.typePicker.noTypeMatchesNarrowing')
          : t('tenant.documents.typePicker.noTypesConfigured'),
        colCode: t('tenant.documentTypes.list.colCode'),
        colName: t('tenant.documentTypes.list.colName'),
        colCompany: t('tenant.documentTypes.list.colCompany'),
        colDescription: t('tenant.documentTypes.list.colDescription'),
        filterCode: t('tenant.documents.typePicker.filterCode'),
        filterName: t('tenant.documents.typePicker.filterName'),
        filterCompany: t('tenant.documents.typePicker.filterCompany'),
        favorites: {
          toggleShowPinned: t('tenant.documents.typePicker.toggleShowPinned'),
          toggleShowFull: t('tenant.documents.typePicker.toggleShowFull'),
          pinTooltip: t('tenant.documents.typePicker.pinTooltip'),
          unpinTooltip: t('tenant.documents.typePicker.unpinTooltip'),
        },
      }}
    />
  );
}
